import { useFiles } from 'features/media/useFiles';
import { SignalRContext } from 'shared/contexts/signalr';
import { FileUploadStatus } from 'shared/typings/file/enums';
import { useContext, useEffect } from 'react';
import { BrkfstFile } from 'shared/typings/file';
import { useFormikContext } from 'formik';
import { get } from 'lodash';

type NewFile = BrkfstFile & { key: string; isBrandAsset: boolean; briefId?: string };
type ThumbnailAddedCallback = (args: { fileId: string; thumbnailUrl: string; thumbnailOffset: string }) => void;

type UploadCompletedCallback = (file: BrkfstFile, key: string) => void;

type Props = {
  onUploadCompleted?: UploadCompletedCallback;
  onThumbnailAdded?: ThumbnailAddedCallback;
  onFileError?: (args: { error: string; key: string }) => void;
  setEventHandlers?: boolean;
  name?: string;
  toggleLoading?: (newValue: boolean) => void;
};

export const useBriefAssetsNotification = (
  { onThumbnailAdded, onUploadCompleted, setEventHandlers = true, name, onFileError }: Props = {
    setEventHandlers: true,
  },
): void => {
  const { connection } = useContext(SignalRContext);
  const { updateFileUpload } = useFiles();

  useEffect(() => {
    const notificationHandler = ({ key, isBrandAsset, ...file }: NewFile) => {
      if (key && isBrandAsset) {
        onUploadCompleted?.(file, key);
        updateFileUpload(key, FileUploadStatus.UPLOADED);
      }
    };

    const thumbnailHandler: ThumbnailAddedCallback = ({ fileId, thumbnailUrl, thumbnailOffset }) => {
      onThumbnailAdded?.({ fileId, thumbnailUrl, thumbnailOffset });
    };

    const fileErrorHandler = ({ error, key }) => {
      onFileError?.({ error, key });
    };

    if (connection && setEventHandlers) {
      connection.on('newFile', notificationHandler);
      connection.on('newThumbnail', thumbnailHandler);
      connection.on('fileError', fileErrorHandler);
    }

    return function cleanup() {
      connection?.off('newFile', notificationHandler);
      connection?.off('newThumbnail', thumbnailHandler);
      connection?.on('fileError', fileErrorHandler);
    };
  }, [connection, setEventHandlers]);
};

export const BriefExampleContentNotification: React.FC<Props & { moduleFieldName: string }> = ({
  moduleFieldName,
  ...props
}) => {
  const { setFieldValue, values } = useFormikContext();

  const onThumbnailAdded = ({ fileId, thumbnailUrl }) => {
    const modules = get(values, moduleFieldName);
    const idx: number = modules.findIndex((mod) => mod?.id === +fileId);
    if (modules[idx]?.id === +fileId) {
      setFieldValue(`${moduleFieldName}[${idx}].exampleContent`, { ...modules[idx], thumbnailUrl });
    }
  };
  const onUploadCompleted = (file, key) => {
    setFieldValue(key, { file, uploading: false });
  };
  const onFileError = ({ error, key }) => {
    setFieldValue(key, { file: null, uploading: false });
  };
  useBriefAssetsNotification({ ...props, onUploadCompleted, onFileError, onThumbnailAdded });

  return null;
};
